import React, { useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  HelpCircle,
  Minus,
  Plus,
  Target,
  TrendingUp,
  Trophy,
} from "lucide-react";
import bettingOptions from "../../../data/p2pBettingOptions.json";
import { labelForP2PField } from "../../../data/p2pFieldLabels";
import { P2P_SECTION_ICONS } from "./p2pCreateConstants";

const isMatch = (sel, section, sub, key) =>
  sel?.section === section && sel?.subSection === sub && sel?.key === key;

const OutcomeChip = ({ active, onClick, children, className = "" }) => (
  <button
    type="button"
    className={`pcm2-chip${active ? " pcm2-chip-on" : ""} ${className}`.trim()}
    onClick={onClick}
  >
    {active ? <CheckCircle2 size={14} aria-hidden /> : null}
    <span>{children}</span>
  </button>
);

const P2PCreateOutcomeStep = ({ fixture, selected, onSelect, stance, onStanceChange }) => {
  const homeName = fixture?.homeTeam?.name || "Home";
  const awayName = fixture?.awayTeam?.name || "Away";
  const [expanded, setExpanded] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const pick = (section, subSection, key) => onSelect({ section, subSection, key });

  return (
    <div className="pcm2-outcome">
      <div className="pcm2-outcome-intro">
        <p className="pcm2-outcome-kicker">Step 1: Your prediction</p>
        <p className="pcm2-outcome-lead">
          Pick the outcome and whether you're backing it{" "}
          <strong>for</strong> or <strong>against</strong>.
        </p>
        <div className="pcm2-stance-row">
          <OutcomeChip
            active={stance === "for"}
            onClick={() => onStanceChange?.("for")}
          >
            For (happens)
          </OutcomeChip>
          <OutcomeChip
            active={stance === "against"}
            onClick={() => onStanceChange?.("against")}
          >
            Against (doesn't happen)
          </OutcomeChip>
        </div>
        <button
          type="button"
          className="pcm2-help-toggle"
          onClick={() => setShowHelp((v) => !v)}
          aria-expanded={showHelp}
        >
          <HelpCircle size={14} />
          {showHelp ? "Hide tip" : "How P2P works"}
        </button>
        {showHelp ? (
          <div className="pcm2-help-card">
            Pick one outcome to back. Friends or the public can bet against you.
            You win if your pick is correct when the match ends.
          </div>
        ) : null}
      </div>

      {/* Match result — hero tri-grid */}
      <section className="pcm2-market pcm2-market-featured">
        <header className="pcm2-market-head">
          <span className="pcm2-market-icon">
            <Trophy size={15} />
          </span>
          <h3>Match result</h3>
          <span className="pcm2-market-tag">Full time</span>
        </header>
        <div className="pcm2-tri-grid">
          {[
            { key: "1", label: homeName, sub: "Home win" },
            { key: "X", label: "Draw", sub: "Level at FT" },
            { key: "2", label: awayName, sub: "Away win" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`pcm2-tri-tile${
                isMatch(selected, "oneXTwo", "fullTime", opt.key) ? " pcm2-tri-tile-on" : ""
              }`}
              onClick={() => pick("oneXTwo", "fullTime", opt.key)}
            >
              <span className="pcm2-tri-key">{opt.key}</span>
              <span className="pcm2-tri-name">{opt.label}</span>
              <span className="pcm2-tri-sub">{opt.sub}</span>
            </button>
          ))}
        </div>
      </section>

      {/* BTTS + O/U side by side on wider, stack on narrow */}
      <div className="pcm2-market-duo">
        <section className="pcm2-market">
          <header className="pcm2-market-head">
            <span className="pcm2-market-icon">
              <Target size={15} />
            </span>
            <h3>Both score</h3>
          </header>
          <div className="pcm2-split-pair">
            {["yes", "no"].map((key) => (
              <OutcomeChip
                key={key}
                active={isMatch(selected, "bothTeamsToScore", "fullTime", key)}
                onClick={() => pick("bothTeamsToScore", "fullTime", key)}
                className="pcm2-chip-wide"
              >
                {key === "yes" ? "Yes" : "No"}
              </OutcomeChip>
            ))}
          </div>
        </section>

        <section className="pcm2-market">
          <header className="pcm2-market-head">
            <span className="pcm2-market-icon">
              <TrendingUp size={15} />
            </span>
            <h3>Goals 2.5</h3>
          </header>
          <div className="pcm2-split-pair">
            <OutcomeChip
              active={isMatch(selected, "overUnderGoals", "fullTime", "over2_5")}
              onClick={() => pick("overUnderGoals", "fullTime", "over2_5")}
              className="pcm2-chip-wide"
            >
              <Plus size={13} aria-hidden /> Over
            </OutcomeChip>
            <OutcomeChip
              active={isMatch(selected, "overUnderGoals", "fullTime", "under2_5")}
              onClick={() => pick("overUnderGoals", "fullTime", "under2_5")}
              className="pcm2-chip-wide"
            >
              <Minus size={13} aria-hidden /> Under
            </OutcomeChip>
          </div>
        </section>
      </div>

      <button
        type="button"
        className="pcm2-more-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span>{expanded ? "Hide all markets" : "Browse all markets"}</span>
        <ChevronDown
          size={16}
          style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
        />
      </button>

      {expanded
        ? Object.entries(bettingOptions).map(([section, sectionData]) => {
            const Icon = P2P_SECTION_ICONS[section] || Target;
            return (
              <section key={section} className="pcm2-market pcm2-market-advanced">
                <header className="pcm2-market-head">
                  <span className="pcm2-market-icon">
                    <Icon size={15} />
                  </span>
                  <h3>{labelForP2PField(section)}</h3>
                </header>
                {Object.entries(sectionData).map(([sub, values]) => {
                  if (!Array.isArray(values)) return null;
                  return (
                    <div key={sub} className="pcm2-sub-block">
                      <span className="pcm2-sub-label">{labelForP2PField(sub)}</span>
                      <div className="pcm2-chip-grid">
                        {values.map((key) => (
                          <OutcomeChip
                            key={`${sub}-${key}`}
                            active={isMatch(selected, section, sub, key)}
                            onClick={() => pick(section, sub, key)}
                          >
                            {labelForP2PField(key) || key}
                          </OutcomeChip>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </section>
            );
          })
        : null}
    </div>
  );
};

export default P2PCreateOutcomeStep;
